import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * PerfumeBottle
 *
 * Key improvements over the original:
 *  - GLB model loaded ONCE on construction, cached — no re-download on every click
 *  - Lights created ONCE, toggled visible/invisible instead of added/removed each time
 *  - removeBottle() properly disposes geometries + materials to free GPU memory
 *  - Removed dat.gui import (was entirely commented out)
 *  - If someone clicks a perfume before the model finishes loading, the call is
 *    queued and fires automatically once the model is ready
 */
export default class PerfumeBottle {
    constructor(scene) {
        this.scene       = scene;
        this.perfumeMesh = null;
        this.labelMesh   = null;

        // Load these once — reused across every popup
        this.normalMapTexture = new THREE.TextureLoader().load('./assets/normalMap.png');

        // GLTF cache — model loaded once, cloned for each popup
        this._gltfCache    = null;
        this._gltfLoading  = true;
        this._pendingCall  = null; // queued addBottle call made before model was ready

        // Lights created once, toggled on/off
        this._bottleLights = null;

        this._preloadModel();
    }

    // ── Preload (called once on construction) ────────────────────────────
    _preloadModel() {
        const loader = new GLTFLoader();
        loader.load(
            './../../models/bottle/bottleFinal.glb',
            (gltf) => {
                this._gltfCache   = gltf;
                this._gltfLoading = false;

                // Fire any queued call that arrived while we were loading
                if (this._pendingCall) {
                    const { image, name } = this._pendingCall;
                    this._pendingCall = null;
                    this.addBottle(image, name);
                }
            },
            undefined,
            (err) => console.error('Bottle GLB load error:', err)
        );
    }

    // ── Public: show bottle ──────────────────────────────────────────────
    addBottle(perfumeImage, perfumeName) {
        // Model not ready yet — queue and return
        if (this._gltfLoading || !this._gltfCache) {
            this._pendingCall = { image: perfumeImage, name: perfumeName };
            return;
        }

        // Clone the cached scene — fast, no network request
        this.perfumeMesh = this._gltfCache.scene.clone(true);
        this.scene.add(this.perfumeMesh);

        this._configureBottleMesh();
        this._showLights();
        this.addPerfumeNameLabel(perfumeName);
    }

    // ── Public: hide and clean up bottle ────────────────────────────────
    removeBottle() {
        if (this.perfumeMesh) {
            this.scene.remove(this.perfumeMesh);

            // Dispose geometry + material of every mesh in the cloned scene
            this.perfumeMesh.traverse(child => {
                if (!child.isMesh) return;
                child.geometry?.dispose();
                const mats = Array.isArray(child.material)
                    ? child.material
                    : [child.material];
                mats.forEach(m => {
                    m?.map?.dispose();
                    m?.normalMap?.dispose();
                    m?.dispose();
                });
            });

            this.perfumeMesh = null;
        }

        // Dispose label texture and geometry
        if (this.labelMesh) {
            this.labelMesh.material?.map?.dispose();
            this.labelMesh.material?.dispose();
            this.labelMesh.geometry?.dispose();
            this.labelMesh = null;
        }

        // Hide lights instead of removing — avoids recreating them next time
        this._hideLights();
    }

    // ── Label ────────────────────────────────────────────────────────────
    addPerfumeNameLabel(perfumeName) {
        const fontSize    = 45;
        const textureSize = 1024;
        const canvas      = document.createElement('canvas');
        const context     = canvas.getContext('2d');
        canvas.width = canvas.height = textureSize;

        context.fillStyle    = '#000000';
        context.font         = `${fontSize}px Yesteryear`;
        context.textAlign    = 'center';
        context.textBaseline = 'alphabetic';
        context.lineWidth    = 3;

        // Word wrap
        const words    = perfumeName.split(' ');
        const maxWidth = textureSize * 0.5;
        let line = '', lines = [];
        for (let i = 0; i < words.length; i++) {
            const test = line + words[i] + ' ';
            if (context.measureText(test).width > maxWidth && i > 0) {
                lines.push(line.trim());
                line = words[i] + ' ';
            } else {
                line = test;
            }
        }
        lines.push(line.trim());

        const lineHeight = fontSize + 9;
        const offsetY    = textureSize / 2 - (lines.length * lineHeight) / 2;
        lines.forEach((l, i) => {
            context.strokeText(l, textureSize / 2, offsetY + i * lineHeight);
            context.fillText(l,   textureSize / 2, offsetY + i * lineHeight);
        });

        const texture = new THREE.CanvasTexture(canvas);

        if (this.labelMesh) {
            // Reuse the existing mesh, just swap the texture
            this.labelMesh.material.map?.dispose();
            this.labelMesh.material.map = texture;
            this.labelMesh.material.needsUpdate = true;
        } else {
            const geo = new THREE.PlaneGeometry(0.1, 0.2);
            const mat = new THREE.MeshStandardMaterial({
                map:         texture,
                normalMap:   this.normalMapTexture,
                transparent: true,
                side:        THREE.DoubleSide,
                roughness:   0.35,
                metalness:   0.25,
            });
            this.labelMesh = new THREE.Mesh(geo, mat);
            this.labelMesh.position.set(0, 0.036, 0.016);
            this.perfumeMesh.add(this.labelMesh);
        }
    }

    // ── Lights: created once, toggled ───────────────────────────────────
    _showLights() {
        if (this._bottleLights) {
            this._bottleLights.forEach(l => { l.visible = true; });
            return;
        }

        const dir = new THREE.DirectionalLight('#ffffff', 4);
        dir.castShadow = true;
        dir.shadow.camera.far = 15;
        dir.shadow.mapSize.set(1024, 1024);
        dir.shadow.normalBias = 0.05;
        dir.position.set(0.42, -0.5, 0.44);

        const point = new THREE.PointLight('#ffbf00', 2.94);
        point.position.set(0.06, 0.3, 2.02);

        const spot = new THREE.SpotLight('#c3dad7', 10);
        spot.position.set(1.04, 4.24, 3.98);

        this._bottleLights = [dir, point, spot];
        this._bottleLights.forEach(l => this.scene.add(l));
    }

    _hideLights() {
        if (this._bottleLights) {
            this._bottleLights.forEach(l => { l.visible = false; });
        }
    }

    // ── Position and scale the bottle mesh ──────────────────────────────
    _configureBottleMesh() {
        if (!this.perfumeMesh) return;

        const isMobile = window.innerWidth < 768;

        this.perfumeMesh.castShadow    = true;
        this.perfumeMesh.receiveShadow = true;
        this.perfumeMesh.rotation.x   = 0.1;
        this.perfumeMesh.rotation.y   = 0;
        this.perfumeMesh.scale.set(20, 20, 20);

        if (isMobile) {
            this.perfumeMesh.scale.set(8, 8, 8);
            this.perfumeMesh.position.y = 1;
        } else {
            this.perfumeMesh.position.set(0, 0, 1);
        }
    }

    // ── Tick: bottle slowly rotates ──────────────────────────────────────
    tick(time) {
        if (this.perfumeMesh) {
            this.perfumeMesh.rotation.y = time * 0.7;
        }
    }
}